/**
 * Map nav routes (from `lib/nav.ts`) to backend AdminResource keys.
 *
 * The catch-all `<Route path={path} element={<ResourceFallback />}/>` in App.tsx
 * uses this map: if a hit, render `ResourceCrudPage` for that resource; otherwise
 * fall through to the lightweight `PlaceholderPage`.
 *
 * Resource keys follow the backend convention `<app_label>__<model_name>`.
 */
export const ROUTE_TO_RESOURCE: Record<
  string,
  { resource: string; title?: string; description?: string }
> = {
  // ---- AI team ----
  '/apps/ai-team/routines': {
    resource: 'super_agents__routine',
    title: 'Routines',
    description:
      'Jobs your agents do by themselves, on a schedule or when something happens. You can also just ask an agent in chat to set one up.'
  },
  '/apps/ai-team/needs-you': {
    resource: 'super_agents__escalation',
    title: 'Needs your input',
    description:
      'Approvals, questions and alerts from the AI team. Open one to approve, say no or answer.'
  },
  '/apps/ai-team/mailboxes': {
    resource: 'super_agents__agentthread',
    title: 'Mailboxes',
    description: 'Every email conversation the agents are handling.'
  },
  '/apps/ai-team/activity': {
    resource: 'super_agents__agentrun',
    title: 'Team activity',
    description:
      'Each time an agent worked: what started it, what it did and what it cost.'
  },
  '/apps/ai-team/briefs': {
    resource: 'super_agents__agentreport',
    title: 'Daily briefs',
    description: "The Chief of Staff's brief and each agent's daily report."
  },
  '/apps/ai-team/drafts': {
    resource: 'super_agents__draft',
    title: 'Drafts',
    description:
      'Posts, newsletters, ads, outreach and agreements waiting for review.'
  },
  '/apps/ai-team/ads': {
    resource: 'super_agents__adplan',
    title: 'Ad campaigns',
    description:
      'Paid campaigns proposed by the ads agents. Spending always needs your yes.'
  },
  '/apps/ai-team/chargebacks': {
    resource: 'super_agents__dispute',
    title: 'Chargebacks',
    description: 'Card disputes from Stripe, worked by Billing.'
  },
  '/apps/ai-team/privacy': {
    resource: 'super_agents__datarequest',
    title: 'Privacy requests',
    description:
      'Requests for a copy of, or deletion of, personal data. 30-day deadline.'
  },
  '/apps/ai-team/permits': {
    resource: 'super_agents__compliancedoc',
    title: 'Permits & agreements',
    description:
      'Health permits, licences and signed agreements for each business. Legal requests, chases and tracks expiry.'
  },
  '/apps/ai-team/copyright': {
    resource: 'super_agents__copyrightclaim',
    title: 'Copyright claims',
    description: 'DMCA notices logged by Legal.'
  },

  // ---- People / identity ----
  '/apps/people': {
    resource: 'api__user',
    title: 'People',
    description:
      'Everyone with an account. Click a person to see their profile and ban, unban or sign them out.'
  },
  '/apps/pricing': {
    resource: 'directory__businessplan',
    title: 'Pricing plans',
    description:
      'What businesses pay and what they get. Price changes are sent to Stripe automatically.'
  },
  '/apps/reserved-handles': {
    resource: 'api__reservedusername',
    title: 'Reserved Handles',
    description: 'Pre-reserved usernames protected for verified users / brands.'
  },
  '/apps/creators': {
    resource: 'creators__creatorprofile',
    title: 'Creators',
    description:
      'Creator profiles with monetization, packages, and partnerships.'
  },
  '/apps/communities': {
    resource: 'communities__community',
    title: 'Communities',
    description: 'User-created communities (subreddits-style).'
  },

  // ---- Posts variants ----
  '/apps/stories': {
    resource: 'social__story',
    title: 'Stories',
    description: '24-hour ephemeral story posts.'
  },
  '/apps/quickies': {
    resource: 'quickies__quickie',
    title: 'Quickies',
    description: 'Short-form video content.'
  },
  '/apps/news': {
    resource: 'social__news',
    title: 'News',
    description: 'Curated news posts.'
  },
  '/apps/articles': {
    resource: 'publications__article',
    title: 'Articles',
    description: 'Long-form publication articles.'
  },

  // ---- Events ----
  '/apps/events': {
    resource: 'social__event',
    title: 'Events',
    description: 'Real-world and virtual events.'
  },
  '/apps/festivals': {
    resource: 'social__festivalsession',
    title: 'Festivals',
    description: 'Multi-day festival sessions and ticket tiers.'
  },
  '/apps/sports': {
    resource: 'social__league',
    title: 'Sports leagues',
    description: 'Amateur sports leagues, teams, and games.'
  },
  '/apps/bar-crawls': {
    resource: 'social__barcrawl',
    title: 'Bar Crawls',
    description: 'Self-guided bar crawl events with stops + check-ins.'
  },
  '/apps/contests': {
    resource: 'contests__kittycontestslot',
    title: 'Contests',
    description: 'Kitty contests, trivia, and prize draws.'
  },
  '/apps/giveaways': {
    resource: 'giveaways__giveaway',
    title: 'Giveaways',
    description: 'Brand giveaways with entry tasks and winners.'
  },

  // ---- Directory / commerce ----
  '/apps/directory': {
    resource: 'directory__businesssubmission',
    title: 'Directory submissions',
    description: 'Pending business submissions awaiting review.'
  },
  '/apps/greenbook': {
    resource: 'directory__businessplan',
    title: 'Greenbook plans',
    description: 'Greenbook subscription plans for businesses.'
  },
  '/apps/menus': {
    resource: 'menu__menu',
    title: 'Menus',
    description: 'Restaurant and food-truck menus.'
  },
  '/apps/catering': {
    resource: 'menu__cateringrequest',
    title: 'Catering requests',
    description: 'Catering inquiries and bookings.'
  },
  '/apps/service-bookings': {
    resource: 'service_booking__servicebooking',
    title: 'Service bookings',
    description: 'Bookings for haircuts, nails, photography, etc.'
  },
  '/apps/spaces': {
    resource: 'spaces__roomtype',
    title: 'Space room types',
    description: 'Room types for hotel/space inventory.'
  },
  '/apps/hotels': {
    resource: 'spaces__shiftswaprequest',
    title: 'Hotel shift swaps',
    description: 'Hotel staff shift swap requests.'
  },
  '/apps/delivery': {
    resource: 'delivery__deliveryrequest',
    title: 'Delivery requests',
    description: 'On-demand delivery jobs.'
  },
  '/apps/rides': {
    resource: 'rides__riderequest',
    title: 'Rides',
    description: 'Ride requests + estimates.'
  },
  '/apps/stocks': {
    resource: 'stocks__position',
    title: 'Stock positions',
    description: 'User stock positions and trades.'
  },

  // ---- Comms ----
  '/apps/calls': {
    resource: 'calls__call',
    title: 'Calls',
    description: 'Voice and video calls.'
  },
  '/apps/messaging': {
    resource: 'messaging__conversation',
    title: 'Messaging',
    description: 'Direct-message conversations.'
  },
  '/apps/notifications': {
    resource: 'notifications__notificationpreference',
    title: 'Notifications',
    description: 'Per-user notification preferences.'
  },
  '/apps/donations': {
    resource: 'api__donationcampaign',
    title: 'Donations',
    description: 'Donation campaigns and backers.'
  },
  '/apps/watch': {
    resource: 'watchthis__episode',
    title: 'Watch episodes',
    description: 'Watch-this series episodes.'
  },
  '/apps/recommendations': {
    resource: 'recommendations__userinterest',
    title: 'User interests',
    description: 'Recommendation engine — per-user interest signals.'
  },
  '/apps/jobs': {
    resource: 'jobs__job',
    title: 'Jobs',
    description: 'Open job listings.'
  },
  '/apps/leaderboards': {
    resource: 'leaderboard__leaderboard',
    title: 'Leaderboards',
    description: 'Leaderboards across the platform.'
  },
  '/apps/publications': {
    resource: 'publications__publication',
    title: 'Publications',
    description: 'Long-form publications and newsletters.'
  }
}

export type RouteResourceMeta =
  (typeof ROUTE_TO_RESOURCE)[keyof typeof ROUTE_TO_RESOURCE]
